import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus, Search, Filter, TrendingUp } from 'lucide-react'
import { usePurchaseOrderPage, useApprovePurchaseOrder, usePlacePurchaseOrder } from '@/hooks/procurement'
import { Loading, InlineLoading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'
import { EmptyData } from '@/components/EmptyState'
import { PURCHASE_STATUS_LABELS, type PurchaseOrderStatus } from '@/api/types/procurement'
import { getApiErrorMessage } from '@/api/client'
import { useToast } from '@/components/ToastProvider'

type StatusFilter =
  | 'all'
  | 'CREATED'
  | 'APPROVED'
  | 'ORDERED'
  | 'IN_TRANSIT'
  | 'PARTIALLY_RECEIVED'
  | 'RECEIVED'

const statusFilters: StatusFilter[] = [
  'all',
  'CREATED',
  'APPROVED',
  'ORDERED',
  'IN_TRANSIT',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
]

export default function PurchaseOrders() {
  const toast = useToast()
  const [searchParams] = useSearchParams()
  const statusFromQuery = searchParams.get('status') as StatusFilter | null
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    statusFromQuery && statusFilters.includes(statusFromQuery) ? statusFromQuery : 'all'
  )
  const [inputKeyword, setInputKeyword] = useState('')
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(0)

  const { data, isLoading, error } = usePurchaseOrderPage({
    status: statusFilter === 'all' ? undefined : statusFilter,
    keyword: keyword || undefined,
    page,
    size: 20,
  })
  const approveMutation = useApprovePurchaseOrder()
  const placeOrderMutation = usePlacePurchaseOrder()

  const statusTabs = [
    { key: 'all' as StatusFilter, label: '全部' },
    { key: 'CREATED' as StatusFilter, label: '待审批' },
    { key: 'APPROVED' as StatusFilter, label: '已审批' },
    { key: 'ORDERED' as StatusFilter, label: '待发货' },
    { key: 'IN_TRANSIT' as StatusFilter, label: '运输中' },
    { key: 'PARTIALLY_RECEIVED' as StatusFilter, label: '部分到货' },
    { key: 'RECEIVED' as StatusFilter, label: '已完成' },
  ]

  const statusMap: Record<PurchaseOrderStatus, { label: string; class: string }> = {
    CREATED: { label: '待审批', class: 'bg-amber-100 text-amber-600' },
    APPROVED: { label: '已审批待下单', class: 'bg-blue-100 text-blue-600' },
    ORDERED: { label: '已下单', class: 'bg-cyan-100 text-cyan-600' },
    IN_TRANSIT: { label: '运输中', class: 'bg-purple-100 text-purple-600' },
    PARTIALLY_RECEIVED: { label: '部分到货', class: 'bg-orange-100 text-orange-600' },
    RECEIVED: { label: '已入库', class: 'bg-green-100 text-green-600' },
    CANCELLED: { label: '已取消', class: 'bg-red-100 text-red-600' },
    CLOSED: { label: '已关闭', class: 'bg-gray-100 text-gray-600' },
  }

  const handleSearch = () => {
    setKeyword(inputKeyword.trim())
    setPage(0)
  }

  const handleApprove = async (id: number) => {
    try {
      await approveMutation.mutateAsync(id)
      toast.success('采购单已审批')
    } catch (mutationError) {
      toast.error(getApiErrorMessage(mutationError))
    }
  }

  const handlePlaceOrder = async (id: number) => {
    try {
      await placeOrderMutation.mutateAsync(id)
      toast.success('采购单已下单')
    } catch (mutationError) {
      toast.error(getApiErrorMessage(mutationError))
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载采购单数据..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <InlineError message={getApiErrorMessage(error)} />
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/supply-chain/purchase/workbench" className="rounded-lg p-2 transition hover:bg-gray-100">
            <TrendingUp className="h-5 w-5 rotate-180 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-gray-900">采购单管理</h1>
            <p className="mt-1 text-sm text-gray-500">采购全流程管理</p>
          </div>
        </div>
        <Link
          to="/supply-chain/purchase/create"
          className="flex items-center gap-2 rounded-xl bg-primary-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-600"
        >
          <Plus className="h-4 w-4" />
          创建采购单
        </Link>
      </div>

      <div className="mb-6 rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex overflow-x-auto border-b border-gray-100">
          {statusTabs.map((tab) => {
            const active = statusFilter === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => {
                  setStatusFilter(tab.key)
                  setPage(0)
                }}
                className={`border-b-2 px-6 py-4 text-sm font-medium transition ${
                  active
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-4 p-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="搜索采购单号、关联工单、供应商..."
              value={inputKeyword}
              onChange={(event) => setInputKeyword(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleSearch()}
              className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={() => {
              setInputKeyword('')
              setKeyword('')
              setStatusFilter('all')
              setPage(0)
            }}
            className="flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-200"
          >
            <Filter className="h-4 w-4" />
            重置
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
        {data?.content.length ? (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-left text-sm text-gray-500">
                <th className="px-5 py-4 font-medium">采购单号</th>
                <th className="px-5 py-4 font-medium">关联工单</th>
                <th className="px-5 py-4 font-medium">供应商</th>
                <th className="px-5 py-4 font-medium">配件数量</th>
                <th className="px-5 py-4 font-medium">预计交付</th>
                <th className="px-5 py-4 font-medium">收货仓库</th>
                <th className="px-5 py-4 font-medium">状态</th>
                <th className="px-5 py-4 font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {data.content.map((order) => (
                <tr key={order.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-4 font-medium text-primary-600">{order.purchaseNo}</td>
                  <td className="px-5 py-4 text-gray-600">{order.workOrderNo || '-'}</td>
                  <td className="px-5 py-4 text-gray-600">{order.supplierName}</td>
                  <td className="px-5 py-4 text-gray-600">{order.items.length} 项</td>
                  <td className="px-5 py-4 text-gray-600">
                    {order.expectedDeliveryDate
                      ? new Date(order.expectedDeliveryDate).toLocaleDateString('zh-CN')
                      : '-'}
                  </td>
                  <td className="px-5 py-4 text-gray-600">{order.warehouseCode || '-'}</td>
                  <td className="px-5 py-4">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${
                        statusMap[order.status as PurchaseOrderStatus]?.class || ''
                      }`}
                    >
                      {statusMap[order.status as PurchaseOrderStatus]?.label ||
                        PURCHASE_STATUS_LABELS[order.status as PurchaseOrderStatus] ||
                        order.status}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/supply-chain/purchase/${order.id}`}
                        className="text-primary-600 hover:underline"
                      >
                        详情
                      </Link>
                      {order.status === 'CREATED' ? (
                        <button
                          onClick={() => handleApprove(order.id)}
                          disabled={approveMutation.isPending}
                          className="text-blue-600 hover:underline disabled:opacity-50"
                        >
                          {approveMutation.isPending ? <InlineLoading /> : '审批'}
                        </button>
                      ) : null}
                      {order.status === 'APPROVED' ? (
                        <button
                          onClick={() => handlePlaceOrder(order.id)}
                          disabled={placeOrderMutation.isPending}
                          className="text-blue-600 hover:underline disabled:opacity-50"
                        >
                          {placeOrderMutation.isPending ? <InlineLoading /> : '下单'}
                        </button>
                      ) : null}
                      {order.status === 'IN_TRANSIT' ? (
                        <Link
                          to={`/supply-chain/purchase/${order.id}/logistics`}
                          className="text-purple-600 hover:underline"
                        >
                          跟踪
                        </Link>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyData description={keyword || statusFilter !== 'all' ? '没有找到符合条件的采购单' : '暂无采购单数据'} />
        )}

        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
          <div className="text-sm text-gray-500">
            显示 {data?.content.length || 0} 条，共 {data?.totalElements || 0} 条记录
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              disabled={page === 0}
              className="rounded border border-gray-200 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40"
            >
              上一页
            </button>
            <span className="text-sm text-gray-600">
              第 {page + 1} / {data?.totalPages || 1} 页
            </span>
            <button
              onClick={() => setPage((current) => Math.min(current + 1, (data?.totalPages || 1) - 1))}
              disabled={page >= (data?.totalPages || 1) - 1}
              className="rounded border border-gray-200 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
